// Orkiestracja: spina magazyn konstrukcji, edytor, symulacje, render i interfejs.
// To jedyne miejsce, w ktorym spotykaja sie te warstwy.

import { CHALLENGES, type ChallengeDefinition } from '../challenges/step';
import { CATALOG } from '../domain/catalog';
import { cloneBlueprint, emptyBlueprint } from '../domain/blueprint';
import { blueprintBoundsGrid } from '../domain/geometry';
import type { RobotBlueprint } from '../domain/types';
import { isReadinessCode, validateBuilding } from '../domain/validate';
import { Editor } from '../editor/editor';
import { Palette } from '../editor/palette';
import { computeCenterOfMass, computeOrigin, robotBodies } from '../physics/compileRobot';
import { createTrial, DEFAULT_TRIAL_OPTIONS, type PropHandle, type Trial } from '../physics/simulation';
import { gridToMeters } from '../physics/units';
import { Camera } from '../render/camera';
import { Stage } from '../render/canvas';
import { drawBlueprint, drawGhost, drawSockets } from '../render/drawPreview';
import { drawRobot } from '../render/drawRobot';
import { drawChallenge, drawProps, drawStartMarker, type PropPose } from '../render/drawWorld';
import { drawOutcomeEffect, outcomeEffectDuration, outcomeShake } from '../render/effects';
import { blueprintCost } from '../domain/costs';
import { buildDebugReportJson } from './debugReport';
import { vibrateOutcome } from './haptics';
import { isTrialOver, outcomeToState, type GameState } from './machine';
import { mapPath, layoutMap } from './mapLayout';
import { loadState, saveState } from './persistence';
import {
  clearProgress,
  emptyProgress,
  frontierChallengeId,
  isCompleted,
  isUnlocked,
  loadProgress,
  markCompleted,
  newlyUnlocked,
  type Progress,
} from './progress';
import { loadRecords, submitRecord, type Records } from './records';
import { loadMuted, playOutcome, resumeAudio, saveMuted } from './sound';
import { BlueprintStore } from './store';

const MAX_STEPS_PER_FRAME = 8;
/** Ruch wiekszy niz tyle pikseli to przeciaganie, nie dotkniecie. */
const TAP_SLOP_PX = 12;
/**
 * Przytrzymanie palca na klocku z katalogu, po ktorym rusza niesienie (dotyk).
 * Krotki ruch palca nadal przewija katalog, a niesienie jest swiadomym gestem -
 * inaczej "przewin katalog" i "przenies klocek" bylyby tym samym gestem.
 */
const PALETTE_HOLD_MS = 220;
/** Ile pokazujemy podglad trasy po wybraniu wezla mapy, zanim wejdziemy do warsztatu. */
const TRACK_PREVIEW_MS = 2000;
/** Co ile odswiezamy odliczanie podgladu trasy. */
const COUNTDOWN_TICK_MS = 100;
/** Po sukcesie wracamy na mape tras (po animacji sukcesu). */
const RETURN_TO_MAP_MS = 2600;
/** Zapas wokol konstrukcji w warsztacie (kratki z kazdej strony) - miejsce na nowe klocki. */
const WORKSHOP_MARGIN_GRID = 2;
/** Minimalny widoczny obszar warsztatu (kratki) - mala konstrukcja nie jest przekadowana. */
const WORKSHOP_MIN_SPAN_GRID = 6;

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Brak elementu #${id}`);
  return element as T;
}

/** Biezacy gest niesienia klocka z katalogu (od pointerdown do pointerup). */
interface PaletteDragSession {
  typeId: string;
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  /** Przycisk katalogu, na ktorym zaczeto gest (cel pointer capture). */
  button: HTMLElement;
  /** Timer przytrzymania na dotyku - niesienie rusza dopiero po jego wywolaniu. */
  holdTimer: ReturnType<typeof setTimeout> | null;
}

export class App {
  private readonly stage: Stage;
  private readonly trackStage: Stage;
  private readonly camera = new Camera();
  private readonly trackCamera = new Camera();
  private readonly store: BlueprintStore;
  private readonly editor: Editor;
  private readonly palette: Palette;
  /** Plotno warsztatu - cel upuszczenia klocka niesionego z katalogu. */
  private readonly stageCanvas = requireElement<HTMLCanvasElement>('stage');
  /** Katalog klockow - tu startuje niesienie klocka na plotno. */
  private readonly paletteEl = requireElement<HTMLElement>('palette');

  private readonly statusEl = requireElement<HTMLParagraphElement>('status');
  private readonly reasonEl = requireElement<HTMLParagraphElement>('reason');
  private readonly reasonWorkshopEl = requireElement<HTMLParagraphElement>('reason-workshop');
  private readonly issuesEl = requireElement<HTMLUListElement>('issues');
  private readonly mapEl = requireElement<HTMLElement>('track-map');
  private readonly resetProgressBtn = requireElement<HTMLButtonElement>('btn-reset-progress');
  private readonly statsEl = requireElement<HTMLParagraphElement>('board-stats');
  /** Budzet warsztatu w rogu plotna (uzupelniany przy kazdej zmianie konstrukcji). */
  private readonly budgetEl = requireElement<HTMLParagraphElement>('workshop-budget');
  private readonly mapBackBtn = requireElement<HTMLButtonElement>('btn-map');
  /** Glosniczek: przelacza dzwiek zwyciestwa/porazki (stan zapamietany w localStorage). */
  private readonly soundBtn = requireElement<HTMLButtonElement>('btn-sound');
  private readonly viewStartEl = requireElement<HTMLElement>('view-start');
  private readonly viewWorkshopEl = requireElement<HTMLElement>('view-workshop');
  private readonly viewTrackEl = requireElement<HTMLElement>('view-track');
  private readonly countdownEl = requireElement<HTMLElement>('track-countdown');
  private readonly countdownTextEl = requireElement<HTMLElement>('track-countdown-text');
  private readonly countdownFillEl = requireElement<HTMLElement>('track-countdown-fill');
  private readonly debugPanelEl = requireElement<HTMLElement>('debug-panel');
  private readonly debugLiveEl = requireElement<HTMLParagraphElement>('debug-live');
  private readonly debugReportEl = requireElement<HTMLTextAreaElement>('debug-report');
  private readonly debugCopyBtn = requireElement<HTMLButtonElement>('btn-debug-copy');
  private readonly debugCloseBtn = requireElement<HTMLButtonElement>('btn-debug-close');
  /** Wezly mapy podrozy (klucz = id trasy) - do klodki, zaliczenia i podswietlenia. */
  private readonly mapNodes = new Map<string, HTMLButtonElement>();
  private readonly testBtn = requireElement<HTMLButtonElement>('btn-test');
  private readonly pauseBtn = requireElement<HTMLButtonElement>('btn-pause');
  private readonly restartBtn = requireElement<HTMLButtonElement>('btn-restart');
  private readonly editBtn = requireElement<HTMLButtonElement>('btn-edit');
  private readonly rotateBtn = requireElement<HTMLButtonElement>('btn-rotate');
  private readonly undoBtn = requireElement<HTMLButtonElement>('btn-undo');
  private readonly redoBtn = requireElement<HTMLButtonElement>('btn-redo');
  /** Kosz na plotnie warsztatu - cel upuszczenia przeciaganego klocka. */
  private readonly trashEl = requireElement<HTMLElement>('trash');

  private trial: Trial | null = null;
  private state: GameState = 'editing';
  private accumulator = 0;
  private lastTimestamp = 0;
  private wasHidden = false;
  private pointerDown: { x: number; y: number } | null = null;
  /** Klocek pod palcem z pointerdown - kandydat do przeciagania po przekroczeniu progu. */
  private dragCandidate: string | null = null;
  /** Czy trwa przeciaganie klocka (po przekroczeniu progu ruchu). */
  private dragging = false;

  /** Biezacy gest niesienia klocka z katalogu (albo null). */
  private paletteDrag: PaletteDragSession | null = null;
  /** Czy klocek z katalogu jest juz niesiony (po progu ruchu albo przytrzymaniu). */
  private paletteDragging = false;
  /** Klik po zakonczeniu niesienia to ten sam gest - nie wchodzimy drugi raz w montaz. */
  private suppressPaletteClick = false;
  /**
   * Blokada przewijania katalogu w trakcie niesienia klocka. Dodawana tylko na czas
   * gestu dotyku - na palcu kazdy ruch w pionie jest niesieniem, nie przewijaniem.
   */
  private readonly preventPaletteScroll = (event: TouchEvent): void => {
    if (this.paletteDragging) event.preventDefault();
  };

  /** Aktywna plansza - do zapisu w localStorage. */
  private activeChallengeId = '';
  /** Konstrukcja per plansza: klucz to id planszy (pamiec miedzy przelaczeniami). */
  private readonly boardBlueprints = new Map<string, RobotBlueprint>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Timer podgladu trasy po wybraniu wezla mapy (auto-przejscie do warsztatu). */
  private previewTimer: ReturnType<typeof setTimeout> | null = null;
  /** Odswiezanie odliczania podgladu trasy. */
  private previewInterval: ReturnType<typeof setInterval> | null = null;
  /** Ile zostalo podgladu trasy (ms) - do odliczania. */
  private previewRemainingMs = 0;
  /** Klatka (frameTime), po ktorej wracamy na mape po sukcesie. null = brak. */
  private returnAtFrame: number | null = null;
  /** Efekt sukcesu/porazki na trasie (animacja po zakonczeniu proby). */
  private outcomeEffect: {
    outcome: 'won' | 'failed';
    startedAt: number;
    x: number;
    y: number;
  } | null = null;
  /** Znacznik czasu biezacej klatki (do animacji efektow). */
  private frameTime = 0;
  /** Rekordy osobiste per plansza (najlepszy czas zwyciestwa). */
  private records: Records = {};
  /** Postep trybu podrozy: ktore trasy sa juz zaliczone (a wiec co jest odblokowane). */
  private progress: Progress = emptyProgress();
  /** Biezacy widok: start (mapa podrozy), warsztat (budowanie) albo trasa (proba). */
  private view: 'start' | 'workshop' | 'track' = 'start';
  /** Tryb debug: podglad liczb z proby + raport do skopiowania (?debug=1). */
  private debugEnabled = false;
  /** Czy dzwiek jest wyciszony (przelacznik glosniczka; zapamietany w localStorage). */
  private soundMuted = false;

  constructor() {
    this.stage = new Stage(this.stageCanvas);
    this.trackStage = new Stage(requireElement<HTMLCanvasElement>('stage-track'));

    const saved = loadState();
    this.progress = loadProgress();

    // Plansza startowa: zapamietana, o ile jest odblokowana w trybie podrozy.
    const initialChallenge = this.initialChallenge(saved?.selectedChallengeId);
    this.activeChallengeId = initialChallenge.id;

    // Pamiec konstrukcji per plansza - przyjmujemy konstrukcje STRUKTURALNIE poprawne.
    // NIE wymagamy gotowosci ani budzetu: gracz moze zapisac niekompletny pojazd
    // (np. bez akumulatora), ktory i tak wypusci na trase. Inaczej zapis przepadal
    // przy ponownym wejsciu na mape.
    if (saved) {
      for (const [id, blueprint] of Object.entries(saved.blueprints)) {
        if (validateBuilding(blueprint).length === 0) {
          this.boardBlueprints.set(id, blueprint);
        }
      }
    }

    this.store = new BlueprintStore(this.initialBlueprint(initialChallenge));
    this.boardBlueprints.set(initialChallenge.id, cloneBlueprint(this.store.current));
    this.store.setBudget(initialChallenge.budgetPln);
    this.store.setLimits(initialChallenge.limits);
    this.records = loadRecords();
    this.soundMuted = loadMuted();

    this.editor = new Editor(this.store, this.camera, (message, kind) => {
      this.setMessage(message, kind);
    });
    this.palette = new Palette(this.paletteEl, CATALOG, (typeId) => {
      // Klik tuz po niesieniu klocka to ten sam gest - nie wchodzimy drugi raz w montaz.
      if (this.suppressPaletteClick) {
        this.suppressPaletteClick = false;
        return;
      }
      if (!this.canEdit) {
        this.setMessage('Najpierw wroc do budowania.', 'error');
        return;
      }
      this.editor.beginPlacement(typeId);
      this.palette.setSelected(this.editor.isPlacing ? typeId : null);
      this.refreshUi();
    });

    this.bindControls();
    this.applySoundState();
    this.bindPointer(this.stageCanvas);
    this.bindPaletteDrag();
    this.bindKeyboard();
    this.store.subscribe(() => this.onStoreChanged());

    document.addEventListener('visibilitychange', () => this.onVisibilityChange());

    this.populateMap();
    this.resetTrial();
    this.refreshUi();
    this.setView(this.view);
    this.scrollToJourneyStart();

    // Tryb debug: wlaczany parametrem ?debug w adresie (albo Ctrl/Cmd+Shift+D).
    const search = typeof globalThis.location === 'undefined' ? '' : globalThis.location.search;
    this.debugEnabled = new URLSearchParams(search).has('debug');
    this.applyDebug();

    this.schedulePersist();
    requestAnimationFrame((timestamp) => this.frame(timestamp));
  }

  /** Kolejnosc tras w trybie podrozy (od pierwszej do ostatniej). */
  private challengeOrder(): string[] {
    return CHALLENGES.map((challenge) => challenge.id);
  }

  /**
   * Plansza startowa: zapamietana, o ile jest odblokowana. Gdy zapis wskazuje na
   * zamknieta trase (np. po resecie postepu), wracamy na najdalsza odblokowana.
   */
  private initialChallenge(savedId: string | undefined): ChallengeDefinition {
    const order = this.challengeOrder();
    const saved = CHALLENGES.find((challenge) => challenge.id === savedId);
    if (saved && isUnlocked(order, this.progress, saved.id)) return saved;

    const frontier = frontierChallengeId(order, this.progress);
    const fallback =
      CHALLENGES.find((challenge) => challenge.id === frontier) ?? CHALLENGES[0];
    if (!fallback) throw new Error('Brak zdefiniowanych plansz');
    return fallback;
  }

  /**
   * Konstrukcja dla planszy: zapamietana praca gracza albo PUSTE plotno.
   * Zadnych gotowych szkieletow - gracz buduje od zera.
   */
  private initialBlueprint(challenge: ChallengeDefinition): RobotBlueprint {
    const stored = this.boardBlueprints.get(challenge.id);
    return stored ? cloneBlueprint(stored) : emptyBlueprint();
  }

  /**
   * Wezly mapy podrozy budowane z rejestru CHALLENGES - klucze nie moga sie rozjechac.
   * Sciezke rysuje SVG (viewBox 0 0 100 100), a wezly to przyciski pozycjonowane
   * procentowo, wiec mapa dziala tak samo na telefonie i na desktopie.
   */
  private populateMap(): void {
    this.mapNodes.clear();

    const points = layoutMap(CHALLENGES.length);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'map-path');
    svg.setAttribute('viewBox', '0 0 100 100');
    // preserveAspectRatio=none + vector-effect: sciezka rozciaga sie z kontenerem,
    // ale grubosc linii zostaje stala.
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    line.setAttribute('d', mapPath(points));
    line.setAttribute('vector-effect', 'non-scaling-stroke');
    svg.append(line);

    const nodes = CHALLENGES.map((challenge, index) => {
      const point = points[index] ?? { index, xPct: 50, yPct: 50 };

      const node = document.createElement('button');
      node.type = 'button';
      node.className = 'node';
      node.dataset.challengeId = challenge.id;
      node.style.left = `${point.xPct}%`;
      node.style.top = `${point.yPct}%`;

      const disc = document.createElement('span');
      disc.className = 'node-disc';

      const number = document.createElement('span');
      number.className = 'node-index';
      number.textContent = String(index + 1);

      const mark = document.createElement('span');
      mark.className = 'node-mark';
      mark.setAttribute('aria-hidden', 'true');

      disc.append(number, mark);

      // Bez podpisu trasy - na mapie zostaja SAME numerki. Nazwa i podpowiedz
      // trasy sa w title/aria-label oraz w naglowku po wejsciu na trase.
      node.append(disc);
      this.mapNodes.set(challenge.id, node);
      return node;
    });

    // Najpierw sciezka (tlo), potem wezly (klikalne) - kolejnosc w DOM = kolejnosc tras.
    this.mapEl.replaceChildren(svg, ...nodes);
    // Mapa rosnie z liczba tras - inaczej wezly nachodzilyby na siebie na telefonie.
    this.mapEl.style.minHeight = `${CHALLENGES.length * 96 + 32}px`;
  }

  private currentChallenge(): ChallengeDefinition {
    const found = CHALLENGES.find((challenge) => challenge.id === this.activeChallengeId);
    if (!found) throw new Error(`Nieznane zadanie: "${this.activeChallengeId}"`);
    return found;
  }

  /** Aktualny identyfikator trasy (wybranej na ekranie startowym). */
  private currentChallengeId(): string {
    return this.activeChallengeId;
  }

  /**
   * Wczytuje konstrukcje jako punkt wyjscia (np. import z kodu albo klonowanie).
   * Publiczne, bo korzysta z tego rowniez automatyzacja i testy.
   */
  loadBlueprint(blueprint: RobotBlueprint): void {
    this.store.load(blueprint);
    this.boardBlueprints.set(this.currentChallengeId(), cloneBlueprint(this.store.current));
    this.backToEditing();
    this.refreshUi();
    this.schedulePersist();
  }

  /**
   * Zmiana planszy: zapamietuje konstrukcje z poprzedniej planszy, laduje wlasciwa
   * dla nowej (z pamieci albo domyslnego robota z garazu) i pokazuje podpowiedz.
   */
  private applyChallenge(challengeId: string): void {
    const previousId = this.activeChallengeId;
    if (previousId && previousId !== challengeId) {
      this.boardBlueprints.set(previousId, cloneBlueprint(this.store.current));
    }

    const challenge = CHALLENGES.find((entry) => entry.id === challengeId);
    if (!challenge) throw new Error(`Nieznane zadanie: "${challengeId}"`);
    this.activeChallengeId = challenge.id;

    // Nowa plansza bez zapisu startuje PUSTA - gracz buduje od zera.
    if (!this.boardBlueprints.has(challenge.id)) {
      this.boardBlueprints.set(challenge.id, emptyBlueprint());
    }

    const blueprint = this.boardBlueprints.get(challenge.id);
    if (blueprint) this.store.load(blueprint);
    this.store.setBudget(challenge.budgetPln);
    this.store.setLimits(challenge.limits);

    this.resetTrial();
    // Warsztat/trasa NIE opisuja trasy tekstem - trase poznaje sie z podgladu,
    // dlatego zamiast podpowiedzi czyscimy komunikaty po poprzedniej planszy.
    this.setMessage('', 'info');
    this.refreshUi();
    this.schedulePersist();
  }

  /**
   * Wybor trasy z mapy podrozy: zamknieta trasa tylko przypomina o kolejnosc,
   * odblokowana laduje sie z podgladem i po kilku sekundach wchodzi do warsztatu.
   */
  private selectTrack(challengeId: string): void {
    if (!isUnlocked(this.challengeOrder(), this.progress, challengeId)) {
      this.setMessage(
        'Ta trasa jest zamknieta. Zalicz poprzednia, aby ja odblokowac.',
        'info',
      );
      return;
    }
    this.applyChallenge(challengeId);
    this.startTrackPreview();
  }

  /** Podglad trasy po wyborze wezla - z odliczaniem, konczy sie wejsciem do warsztatu. */
  private startTrackPreview(): void {
    this.setView('track');
    this.previewRemainingMs = TRACK_PREVIEW_MS;
    this.refreshCountdown();

    this.previewInterval = setInterval(() => {
      this.previewRemainingMs = Math.max(0, this.previewRemainingMs - COUNTDOWN_TICK_MS);
      this.refreshCountdown();
    }, COUNTDOWN_TICK_MS);

    this.previewTimer = setTimeout(() => {
      this.previewTimer = null;
      this.stopCountdown();
      if (this.view === 'track' && !this.trial) this.setView('workshop');
    }, TRACK_PREVIEW_MS);
  }

  /** Anuluje zaplanowany powrot z podgladu (np. gdy gracz sam zmieni widok). */
  private cancelTrackPreview(): void {
    if (this.previewTimer !== null) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
    this.stopCountdown();
  }

  /** Zatrzymuje odliczanie podgladu i chowa jego pasek. */
  private stopCountdown(): void {
    if (this.previewInterval !== null) {
      clearInterval(this.previewInterval);
      this.previewInterval = null;
    }
    this.previewRemainingMs = 0;
    this.refreshCountdown();
  }

  /** Pokazuje i aktualizuje odliczanie do wejscia w Warsztat. */
  private refreshCountdown(): void {
    const remaining = this.previewRemainingMs;
    if (remaining <= 0) {
      this.countdownEl.hidden = true;
      this.countdownTextEl.textContent = '';
      this.countdownFillEl.style.width = '0%';
      return;
    }

    this.countdownEl.hidden = false;
    const seconds = (remaining / 1000).toFixed(1).replace('.', ',');
    this.countdownTextEl.textContent = `${this.currentChallenge().label} · Warsztat za ${seconds} s`;
    const ratio = Math.max(0, Math.min(1, remaining / TRACK_PREVIEW_MS));
    this.countdownFillEl.style.width = `${(ratio * 100).toFixed(1)}%`;
  }

  /** Po sukcesie wracamy na mape podrozy - najpierw pokazujemy animacje sukcesu. */
  private scheduleReturnToMap(): void {
    this.returnAtFrame = this.frameTime + RETURN_TO_MAP_MS;
  }

  private cancelReturnToMap(): void {
    this.returnAtFrame = null;
  }

  /** Po sukcesie (i animacji) sam wracamy na mape podrozy. */
  private maybeReturnToMap(): void {
    if (this.returnAtFrame === null) return;
    if (this.frameTime < this.returnAtFrame) return;
    this.returnAtFrame = null;
    if (this.state !== 'won') return;
    this.backToEditing();
    this.setView('start');
    this.revealFrontier();
  }

  // --- tryb debug (raport z proby do skopiowania) ---

  /** Wlacza/wylacza tryb debug. Publiczne, bo korzysta z tego tez automatyzacja. */
  setDebug(enabled: boolean): void {
    this.debugEnabled = enabled;
    this.applyDebug();
  }

  private applyDebug(): void {
    this.debugPanelEl.hidden = !this.debugEnabled;
    if (this.debugEnabled) this.refreshDebugLive();
    else this.debugLiveEl.textContent = '';
  }

  /** Zywy podglad liczb z biezacej proby. */
  private refreshDebugLive(): void {
    if (!this.debugEnabled) return;
    const trial = this.trial;
    if (!trial) {
      this.debugLiveEl.textContent = `debug · trasa=${this.currentChallengeId()} · stan=${this.state} · brak proby`;
      return;
    }
    const sample = trial.sample;
    this.debugLiveEl.textContent =
      `debug · trasa=${this.currentChallengeId()} · stan=${this.state} · t=${trial.elapsedSec.toFixed(2)}s · ` +
      `minX=${sample.minX.toFixed(2)} maxX=${sample.maxX.toFixed(2)} minY=${sample.minY.toFixed(3)} ` +
      `kat=${sample.angle.toFixed(2)} v=${sample.speed.toFixed(2)} · wynik=${trial.outcome}`;
  }

  /** Zapisuje raport z zakonczonej proby (konstrukcja + przebieg) do skopiowania. */
  private captureDebugReport(trial: Trial): void {
    this.debugReportEl.value = buildDebugReportJson({
      challenge: this.currentChallenge(),
      blueprint: this.store.current,
      outcome: trial.outcome,
      reason: trial.reason,
      elapsedSec: trial.elapsedSec,
      samples: trial.samples,
      sampleEverySec: DEFAULT_TRIAL_OPTIONS.sampleEverySec,
      brakeTorqueFactor: DEFAULT_TRIAL_OPTIONS.brakeTorqueFactor,
      userAgent: typeof globalThis.navigator === 'undefined' ? '' : globalThis.navigator.userAgent,
      nowIso: new Date().toISOString(),
    });
  }

  private async copyDebugReport(): Promise<void> {
    const text = this.debugReportEl.value;
    if (text.length === 0) return;

    try {
      await globalThis.navigator?.clipboard?.writeText(text);
      this.setMessage('Skopiowano raport debug do schowka.', 'info');
      return;
    } catch {
      // Brak dostepu do schowka (np. http bez HTTPS) - zaznaczamy do recznego kopiowania.
    }
    this.debugReportEl.focus();
    this.debugReportEl.select();
    this.setMessage('Zaznaczono raport - skopiuj recznie (Ctrl/Cmd+C).', 'info');
  }

  /** Wiersz statystyk planszy: koszt konstrukcji, cel mistrza i rekord gracza. */
  private refreshStats(): void {
    const challenge = this.currentChallenge();
    const cost = blueprintCost(this.store.current);
    const record = this.records[challenge.id];
    const parts = [
      `Koszt: ${cost} / ${challenge.budgetPln}`,
      `Mistrz: ${challenge.parTimeSec.toFixed(2)} s`,
      record !== undefined ? `Twoj rekord: ${record.toFixed(2)} s` : 'Twoj rekord: -',
    ];
    this.statsEl.textContent = parts.join('   ');
    this.statsEl.dataset.over = String(cost > challenge.budgetPln);

    // Budzet w rogu plotna: ile jeszcze wolno wydac (0 = nie dodasz nic wiecej).
    const remaining = challenge.budgetPln - cost;
    this.budgetEl.textContent = `Budzet: ${remaining} / ${challenge.budgetPln}`;
    this.budgetEl.dataset.empty = String(remaining <= 0);

    this.refreshMap();
  }

  /**
   * Wezly mapy podrozy: klodka (zamknieta), znaczek (zaliczona) i podswietlenie
   * aktywnej trasy. Nazwa trasy i podpowiedz ida do title/aria-label.
   */
  private refreshMap(): void {
    const order = this.challengeOrder();

    CHALLENGES.forEach((challenge, index) => {
      const node = this.mapNodes.get(challenge.id);
      if (!node) return;

      const locked = !isUnlocked(order, this.progress, challenge.id);
      const done = isCompleted(this.progress, challenge.id);
      const active = challenge.id === this.activeChallengeId;

      node.classList.toggle('is-locked', locked);
      node.classList.toggle('is-done', done && !locked);
      node.classList.toggle('is-active', active);
      node.setAttribute('aria-disabled', String(locked));

      const mark = node.querySelector<HTMLElement>('.node-mark');
      if (mark) mark.textContent = locked ? '🔒' : done ? '✓' : '';

      node.setAttribute(
        'aria-label',
        `Trasa ${index + 1}: ${challenge.label}${
          locked ? ' (zamknieta)' : done ? ' (zaliczona)' : ''
        }`,
      );
      node.title = locked ? 'Zalicz poprzednia trase, aby odblokowac.' : challenge.hint;
    });
  }

  /** Przewija mape do wezla (po odblokowaniu) - bez tego gracz nie widzi nowej trasy. */
  private revealNode(challengeId: string): void {
    const node = this.mapNodes.get(challengeId);
    if (!node) return;
    try {
      node.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    } catch {
      // jsdom i starsze przegladarki nie maja scrollIntoView - brak przewijania to nie blad
    }
  }

  /** Start podrozy jest na dole mapy - od razu pokazujemy pierwsza trase. */
  private scrollToJourneyStart(): void {
    this.viewStartEl.scrollTop = this.viewStartEl.scrollHeight;
  }

  /** Po powrocie na mape pokazujemy trase, ktora gracz ma teraz zrobic. */
  private revealFrontier(): void {
    const frontier = frontierChallengeId(this.challengeOrder(), this.progress);
    if (frontier) this.revealNode(frontier);
  }

  /** Przelacza widok: "start" (mapa), "workshop" (budowanie) albo "track" (proba). */
  private setView(view: 'start' | 'workshop' | 'track'): void {
    // Kazda zmiana widoku z zewnatrz anuluje zaplanowane powroty.
    this.cancelTrackPreview();
    this.cancelReturnToMap();
    this.view = view;
    this.viewStartEl.hidden = view !== 'start';
    this.viewWorkshopEl.hidden = view !== 'workshop';
    this.viewTrackEl.hidden = view !== 'track';

    // Nad mapa nie ma dokad wracac; w warsztacie i na trasie przycisk wraca do mapy.
    this.mapBackBtn.hidden = view === 'start';

    // Rysujemy od razu widoczny widok - po zmianie canvas nie czeka na klatke.
    this.render();
  }

  /** Powrot na mape podrozy - warsztat i trasa sa przypisane do wybranej trasy. */
  private goToMap(): void {
    this.backToEditing();
    this.setView('start');
    this.revealFrontier();
  }

  // --- zapis w localStorage ---

  /** Zbiera konstrukcje wszystkich plansz i zapisuje stan. */
  private persist(): void {
    this.boardBlueprints.set(this.currentChallengeId(), cloneBlueprint(this.store.current));
    saveState(this.currentChallengeId(), Object.fromEntries(this.boardBlueprints));
  }

  /** Opozniony zapis - nie piszemy do localStorage przy kazdym kliknieciu. */
  private schedulePersist(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.persist();
    }, 250);
  }

  private bindControls(): void {
    // Wybor trasy: wezly mapy podrozy (delegacja zdarzen).
    this.mapEl.addEventListener('click', (event) => {
      const target = event.target as HTMLElement | null;
      const node = target?.closest<HTMLElement>('.node');
      const id = node?.dataset.challengeId;
      if (id) this.selectTrack(id);
    });

    // Reset postepu (tryb testowy): odblokowana zostaje tylko pierwsza trasa.
    this.resetProgressBtn.addEventListener('click', () => {
      clearProgress();
      this.progress = emptyProgress();
      const first = CHALLENGES[0];
      if (first && this.activeChallengeId !== first.id) this.applyChallenge(first.id);
      else this.backToEditing();
      this.refreshMap();
      this.setMessage('Zresetowano postep: odblokowana jest tylko pierwsza trasa.', 'info');
    });

    // Tryb debug: kopiowanie raportu i zamkniecie panelu.
    this.debugCopyBtn.addEventListener('click', () => {
      void this.copyDebugReport();
    });
    this.debugCloseBtn.addEventListener('click', () => this.setDebug(false));

    // Glosniczek: przelacza dzwiek i od razu zapamietuje wybor gracza.
    this.soundBtn.addEventListener('click', () => {
      this.soundMuted = !this.soundMuted;
      saveMuted(this.soundMuted);
      this.applySoundState();
      this.setMessage(this.soundMuted ? 'Dzwiek wylaczony.' : 'Dzwiek wlaczony.', 'info');
    });

    this.testBtn.addEventListener('click', () => this.startTrial());
    this.pauseBtn.addEventListener('click', () => this.togglePause());
    this.restartBtn.addEventListener('click', () => {
      this.resetTrial();
      this.startTrial();
    });
    this.editBtn.addEventListener('click', () => {
      this.backToEditing();
      this.setView('workshop');
    });

    // Powrot do mapy (nawigacja kontekstowa - brak globalnych zakladek).
    this.mapBackBtn.addEventListener('click', () => this.goToMap());

    this.rotateBtn.addEventListener('click', () => {
      if (this.editor.isPlacing || this.editor.isDragging) this.editor.cycleRotation();
      else this.editor.rotateSelected();
      this.refreshUi();
    });
    this.undoBtn.addEventListener('click', () => {
      if (this.store.undo()) this.setMessage('Cofnieto zmiane.', 'info');
      this.refreshUi();
    });
    this.redoBtn.addEventListener('click', () => {
      if (this.store.redo()) this.setMessage('Ponowiono zmiane.', 'info');
      this.refreshUi();
    });
  }

  /**
   * Obsluga wskaznika: Pointer Events dzialaja i dla myszy, i dla dotyku.
   * Ruch daje podglad, krotkie dotkniecie zatwierdza montaz albo zaznacza klocek,
   * a przeciagniecie istniejacego klocka przenosi go (albo wyrzuca do kosza).
   */
  private bindPointer(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('pointermove', (event) => {
      const rect = canvas.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const start = this.pointerDown;

      // Przeciaganie zaczyna sie dopiero po przekroczeniu progu ruchu - inaczej
      // zwykly dotyk/klik bylby nierozroznialny od chwytania klocka.
      if (start && !this.dragging && this.dragCandidate) {
        if (Math.hypot(point.x - start.x, point.y - start.y) > TAP_SLOP_PX) {
          this.dragging = this.editor.beginDrag(this.dragCandidate, point.x, point.y);
          if (this.dragging) {
            this.capturePointer(canvas, event.pointerId);
            this.setTrashDragging(true);
          }
        }
      }

      if (this.dragging) {
        this.editor.updateDrag(point.x, point.y);
        this.setTrashHot(this.isOverTrash(event.clientX, event.clientY));
        return;
      }
      this.editor.setPointer(point.x, point.y);
    });

    canvas.addEventListener('pointerdown', (event) => {
      const rect = canvas.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      this.pointerDown = point;
      this.dragging = false;
      this.dragCandidate =
        this.canEdit && !this.editor.isPlacing ? this.editor.partAt(point.x, point.y) : null;
    });

    canvas.addEventListener('pointerup', (event) => {
      const rect = canvas.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      const start = this.pointerDown;
      this.pointerDown = null;

      // Zakonczenie przeciagania: nad koszem usuwamy, w innym miejscu przenosimy.
      if (this.dragging) {
        const partId = this.editor.draggingPart;
        const overTrash = this.isOverTrash(event.clientX, event.clientY);
        this.dragging = false;
        this.dragCandidate = null;
        this.setTrashDragging(false);
        if (partId) {
          if (overTrash) this.editor.deletePart(partId);
          else this.editor.confirmDrag();
        }
        this.editor.clearPointer();
        this.refreshUi();
        return;
      }

      this.dragCandidate = null;
      if (!start) return;
      if (Math.hypot(point.x - start.x, point.y - start.y) > TAP_SLOP_PX) return;
      if (!this.canEdit) return;
      this.editor.handleTap(point.x, point.y);
      // Po postawieniu klocka tryb montazu sie konczy - gasimy tez wybor w palecie.
      this.palette.setSelected(this.editor.placing);
      this.refreshUi();
    });

    canvas.addEventListener('pointercancel', () => {
      this.pointerDown = null;
      this.dragCandidate = null;
      if (!this.dragging) return;
      this.dragging = false;
      this.editor.cancelDrag();
      this.editor.clearPointer();
      this.setTrashDragging(false);
    });

    canvas.addEventListener('pointerleave', () => {
      // W trakcie przeciagania pointer capture trzyma gest - nie czyscimy podgladu.
      if (this.dragging) return;
      this.editor.clearPointer();
    });
  }

  /**
   * Niesienie klocka z katalogu WPROST na plotno - zamiast "wybierz z listy, potem
   * kliknij na plotnie".
   *
   * Mysz i rysik: klocek rusza po przekroczeniu progu ruchu (naturalne "zlap i nies").
   * Dotyk: klocek rusza po krotkim PRZYTRZYMANIU - inaczej kazdy ruch palca po
   * katalogu bylby jednoczesnie przewijaniem i niesieniem. Szybki ruch palca nadal
   * przewija katalog, a samo dotkniecie (bez ruchu) wybiera klocek jak dotad.
   *
   * W trakcie niesienia podglad ("duch") idzie za wskaznikiem i przyciaga sie do
   * wolnych mocowan - ten sam mechanizm co dotychczasowy montaz.
   */
  private bindPaletteDrag(): void {
    this.paletteEl.addEventListener('pointerdown', (event) => {
      if (!this.canEdit || this.paletteDragging) return;
      const button = (event.target as HTMLElement | null)?.closest<HTMLElement>('.part');
      const typeId = button?.dataset.typeId;
      if (!button || !typeId) return;

      // Kazde nowe dotkniecie uniewaznia tlumienie kliku z poprzedniego gestu.
      this.suppressPaletteClick = false;
      this.endPaletteDrag();

      const session: PaletteDragSession = {
        typeId,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        button,
        holdTimer: null,
      };
      this.paletteDrag = session;

      // Dotyk: niesienie startuje po przytrzymaniu, zeby nie kolidowac z przewijaniem.
      if (event.pointerType === 'touch') {
        session.holdTimer = setTimeout(() => {
          if (this.paletteDrag === session && !this.paletteDragging) {
            this.startPaletteDrag(session);
          }
        }, PALETTE_HOLD_MS);
      }
    });

    window.addEventListener('pointermove', (event) => {
      const session = this.paletteDrag;
      if (!session || session.pointerId !== event.pointerId) return;

      if (!this.paletteDragging) {
        const moved = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
        if (moved <= TAP_SLOP_PX) return;
        // Dotyk: ruch palca przed przytrzymaniem to przewijanie katalogu, nie niesienie.
        if (session.pointerType === 'touch') {
          this.endPaletteDrag();
          return;
        }
        this.startPaletteDrag(session);
      }

      // Podglad rysujemy tylko nad plotnem - poza nim "duch" nie ma gdzie sie pokazac.
      if (this.isOverStage(event.clientX, event.clientY)) {
        const point = this.canvasLocalPoint(event.clientX, event.clientY);
        this.editor.setPointer(point.x, point.y);
      } else {
        this.editor.clearPointer();
      }
      event.preventDefault();
    });

    const finish = (event: PointerEvent): void => {
      const session = this.paletteDrag;
      if (!session || session.pointerId !== event.pointerId) return;

      // Zwykly dotyk/klik (bez niesienia) - montaz obsluguje zdarzenie "click".
      if (!this.paletteDragging) {
        this.endPaletteDrag();
        return;
      }

      this.paletteDragging = false;
      this.paletteDrag = null;
      if (session.holdTimer !== null) clearTimeout(session.holdTimer);
      this.dropPalettePart(event.clientX, event.clientY);
    };

    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  }

  /** Start niesienia: tryb montazu, podswietlenie klocka i (na dotyk) blokada przewijania. */
  private startPaletteDrag(session: PaletteDragSession): void {
    if (session.holdTimer !== null) {
      clearTimeout(session.holdTimer);
      session.holdTimer = null;
    }
    this.paletteDragging = true;
    this.suppressPaletteClick = true;
    this.editor.beginPlacement(session.typeId);
    this.palette.setSelected(session.typeId);
    this.paletteEl.classList.add('is-dragging');
    this.capturePointer(session.button, session.pointerId);
    if (session.pointerType === 'touch') {
      // Podczas niesienia palec nie moze jednoczesnie przewijac katalogu.
      window.addEventListener('touchmove', this.preventPaletteScroll, { passive: false });
    }
  }

  /** Upuszczenie niesionego klocka: nad plotnem montuje, poza nim anuluje montaz. */
  private dropPalettePart(clientX: number, clientY: number): void {
    if (this.isOverStage(clientX, clientY)) {
      const point = this.canvasLocalPoint(clientX, clientY);
      this.editor.setPointer(point.x, point.y);
      this.editor.confirm();
    } else {
      this.editor.cancelPlacement();
    }
    this.stopPaletteDragVisuals();
    this.palette.setSelected(this.editor.placing);
    this.refreshUi();
  }

  /** Sprzata wizualia niesienia (kursor, blokada przewijania, podglad). */
  private stopPaletteDragVisuals(): void {
    this.paletteEl.classList.remove('is-dragging');
    window.removeEventListener('touchmove', this.preventPaletteScroll);
    this.editor.clearPointer();
  }

  /** Konczy sesje gestu bez zmiany konstrukcji (np. gdy palec przewija katalog). */
  private endPaletteDrag(): void {
    const session = this.paletteDrag;
    if (session?.holdTimer != null) clearTimeout(session.holdTimer);
    this.paletteDrag = null;
  }

  /** Punkt (x, y) wzgledem plotna warsztatu dla wspolrzednych okna. */
  private canvasLocalPoint(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.stageCanvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  /** Czy wskaznik (we wspolrzednych okna) jest nad plotnem warsztatu. */
  private isOverStage(clientX: number, clientY: number): boolean {
    const rect = this.stageCanvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;
    return (
      clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
    );
  }

  /** Pointer capture trzyma gest nawet gdy palec wyjdzie poza element (np. nad kosz). */
  private capturePointer(element: Element, pointerId: number): void {
    try {
      element.setPointerCapture?.(pointerId);
    } catch {
      // Niektore srodowiska (jsdom) nie obsluguja pointer capture - gest i tak dziala.
    }
  }

  /** Czy wskaznik (we wspolrzednych okna) jest nad koszem. */
  private isOverTrash(clientX: number, clientY: number): boolean {
    const rect = this.trashEl.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return false;
    return (
      clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
    );
  }

  /** W trakcie przeciagania kosz jest widoczny/aktywny, a nad nim podswietlony. */
  private setTrashDragging(active: boolean): void {
    this.trashEl.classList.toggle('is-armed', active);
    if (!active) this.trashEl.classList.remove('is-hot');
  }

  private setTrashHot(hot: boolean): void {
    this.trashEl.classList.toggle('is-hot', hot);
  }

  private bindKeyboard(): void {
    window.addEventListener('keydown', (event) => {
      const key = event.key.toLowerCase();
      const undoCombo = (event.ctrlKey || event.metaKey) && key === 'z';
      const debugCombo = (event.ctrlKey || event.metaKey) && event.shiftKey && key === 'd';

      if (debugCombo) {
        event.preventDefault();
        this.setDebug(!this.debugEnabled);
        return;
      }

      if (undoCombo) {
        if (event.shiftKey) this.store.redo();
        else this.store.undo();
      } else if (event.key === 'Escape') {
        this.editor.cancelDrag();
        this.editor.cancelPlacement();
        this.palette.setSelected(null);
      } else if (!this.canEdit) {
        return;
      } else if (key === 'r') {
        if (this.editor.isPlacing || this.editor.isDragging) this.editor.cycleRotation();
        else this.editor.rotateSelected();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        this.editor.deleteSelected();
      } else {
        return;
      }

      event.preventDefault();
      this.refreshUi();
    });
  }

  /** Zmiany konstrukcji sa mozliwe tylko w trybie budowania. */
  private get canEdit(): boolean {
    return this.state === 'editing';
  }

  /** Biezaca konstrukcja (tylko do odczytu - m.in. dla testow okablowania). */
  get blueprint(): RobotBlueprint {
    return this.store.current;
  }

  private onStoreChanged(): void {
    this.editor.refresh();
    this.refreshUi();
    this.schedulePersist();
  }

  private setMessage(message: string, kind: 'info' | 'error'): void {
    // Komunikat widoczny w obu widokach: na trasie i w warsztacie.
    for (const element of [this.reasonEl, this.reasonWorkshopEl]) {
      element.textContent = message;
      element.dataset.kind = kind;
    }
  }

  private setStatus(text: string, outcome: 'won' | 'failed' | undefined): void {
    this.statusEl.textContent = text;
    if (outcome) {
      this.statusEl.dataset.outcome = outcome;
    } else {
      delete this.statusEl.dataset.outcome;
    }
  }

  /**
   * Odzwierciedla stan glosniczka w przycisku: ikone (aria-pressed), etykiete i
   * tytul. Wyciszony = przekreslony glosnik, wlaczony = fale.
   */
  private applySoundState(): void {
    const on = !this.soundMuted;
    this.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    this.soundBtn.setAttribute('aria-label', on ? 'Wycisz dzwiek' : 'Wlacz dzwiek');
    this.soundBtn.title = on ? 'Dzwiek wlaczony' : 'Dzwiek wyciszony';
  }

  // --- proba ---

  /** Czysci swiat symulacji i wraca do budowania. */
  private resetTrial(): void {
    this.accumulator = 0;
    this.lastTimestamp = 0;
    this.trial = null;
    this.state = 'editing';
    this.outcomeEffect = null;
    this.cancelReturnToMap();
  }

  private startTrial(): void {
    if (this.state === 'running') return;
    // Gest gracza ("Testuj na trase") odblokowuje dzwiek na iOS/Chrome.
    resumeAudio();
    this.outcomeEffect = null;
    this.cancelReturnToMap();

    // Wznowienie po pauzie - swiat juz istnieje.
    if (this.state === 'paused' && this.trial) {
      this.state = 'running';
      this.lastTimestamp = 0;
      this.accumulator = 0;
      this.setStatus('Proba trwa', undefined);
      this.setView('track');
      this.refreshUi();
      return;
    }

    const issues = this.store.trialIssues();
    if (issues.length > 0) {
      this.setStatus('Konstrukcja niepoprawna', 'failed');
      this.setMessage(issues[0]?.message ?? 'Popraw konstrukcje.', 'error');
      this.refreshUi();
      return;
    }

    const created = createTrial(
      this.store.current,
      this.currentChallenge(),
      DEFAULT_TRIAL_OPTIONS,
      { allowIncomplete: true },
    );
    if (!created.ok) {
      this.setStatus('Konstrukcja niepoprawna', 'failed');
      this.setMessage(created.issues[0]?.message ?? 'Popraw konstrukcje.', 'error');
      this.refreshUi();
      return;
    }

    this.trial = created.trial;
    this.state = 'running';
    this.accumulator = 0;
    this.lastTimestamp = 0;
    this.setStatus('Proba trwa', undefined);
    // Niegotowa konstrukcja (np. bez akumulatora) tez wjezdza na trase - uprzedzamy,
    // ze moze nie ruszyc, zamiast blokowac probe.
    const readiness = this.store.issues().filter((issue) => isReadinessCode(issue.code));
    this.setMessage(
      readiness.length > 0
        ? 'Konstrukcja niekompletna - pojazd moze nie ruszyc. Probujemy!'
        : 'Proba w toku.',
      'info',
    );
    this.setView('track');
    this.refreshUi();
  }

  private togglePause(): void {
    if (this.state === 'running') {
      this.state = 'paused';
      this.accumulator = 0;
      this.setStatus('Pauza', undefined);
    } else if (this.state === 'paused') {
      this.state = 'running';
      this.lastTimestamp = 0;
      this.accumulator = 0;
      this.setStatus('Proba trwa', undefined);
    }
    this.refreshUi();
  }

  private backToEditing(): void {
    this.trial = null;
    this.state = 'editing';
    this.accumulator = 0;
    this.outcomeEffect = null;
    this.cancelReturnToMap();
    this.setStatus('Gotowe do proby', undefined);
    this.refreshUi();
  }

  // --- interfejs ---

  private refreshUi(): void {
    // Pokazujemy TYLKO bledy blokujace (struktura konstrukcji + budzet). Braki
    // gotowosci (np. "brak kola", "osadz kolo na wale silnika", "wymaga
    // akumulatora") celowo POMIJAMY - gracz uczy sie ich z miniaturek klockow
    // i z samej proby, bez sciany tekstu w warsztacie.
    const blockers = this.store.trialIssues();

    if (blockers.length === 0) {
      const item = document.createElement('li');
      item.className = 'ok';
      item.textContent = 'Brak bledow - mozesz testowac.';
      this.issuesEl.replaceChildren(item);
    } else {
      const items = blockers.map((issue) => {
        const item = document.createElement('li');
        item.textContent = issue.message;
        return item;
      });
      this.issuesEl.replaceChildren(...items);
    }

    // Katalog: limity trasy + liczniki (klocki na wyczerpanym limicie sa nieaktywne).
    this.palette.setLimits(this.store.partLimits);
    this.palette.updateCounts(this.store.current);

    const editing = this.canEdit;
    const running = this.state === 'running';
    const paused = this.state === 'paused';
    const over = isTrialOver(this.state);

    this.testBtn.disabled = running;
    this.pauseBtn.disabled = !running && !paused;
    this.pauseBtn.textContent = paused ? 'Wznow' : 'Pauza';
    this.restartBtn.disabled = !editing && !running && !paused && !over;
    // "Warsztat" jest w widoku Trasy i ma zawsze dzialac - takze w podgladzie
    // trasy (przed odliczaniem), zeby moc od razu zaczac budowac.
    this.editBtn.disabled = false;
    this.rotateBtn.disabled = !editing;
    this.undoBtn.disabled = !editing || !this.store.canUndo;
    this.redoBtn.disabled = !editing || !this.store.canRedo;

    this.refreshStats();
  }

  /**
   * Kamera warsztatu: dopasowana do obrysu konstrukcji z ZAPASEM na dokladanie
   * klockow. Bez zapasu belka 4x1 wypelniala niemal cala szerokosc i nie bylo
   * gdzie dostawic wspornika osi z kolem (zwlaszcza na telefonie).
   */
  private fitWorkshopCamera(width: number, height: number): void {
    const blueprint = this.store.current;
    if (blueprint.parts.length === 0) {
      this.camera.scale = 200;
      this.camera.centerX = 0;
      this.camera.centerY = 0;
      return;
    }

    const bounds = blueprintBoundsGrid(blueprint);
    const minX = gridToMeters(bounds.minX);
    const maxX = gridToMeters(bounds.maxX);
    const minY = gridToMeters(bounds.minY);
    const maxY = gridToMeters(bounds.maxY);

    const margin = gridToMeters(WORKSHOP_MARGIN_GRID);
    const minSpan = gridToMeters(WORKSHOP_MIN_SPAN_GRID);
    const spanX = Math.max(minSpan, maxX - minX + margin * 2);
    const spanY = Math.max(minSpan, maxY - minY + margin * 2);

    this.camera.scale = Math.max(1, Math.min(width / spanX, height / spanY));
    this.camera.centerX = (minX + maxX) / 2;
    this.camera.centerY = (minY + maxY) / 2;
  }

  /** Statyczna ramka na torze - zakres i wysokosc z definicji zadania. */
  private fitTrackCamera(width: number): void {
    const view = this.currentChallenge().view;
    const spanMeters = view.maxX - view.minX;
    this.trackCamera.scale = Math.max(1, width / spanMeters);
    this.trackCamera.centerX = (view.minX + view.maxX) / 2;
    this.trackCamera.centerY = view.centerY;
  }

  private onVisibilityChange(): void {
    if (document.hidden) {
      this.wasHidden = true;
      this.accumulator = 0;
      if (this.state === 'running') {
        this.state = 'paused';
        this.setStatus('Pauza (karta nieaktywna)', undefined);
        this.refreshUi();
      }
    } else if (this.wasHidden) {
      // Po powrocie nie nadrabiamy zaleglego czasu ani nie skaczemy robotem.
      this.accumulator = 0;
      this.lastTimestamp = 0;
      this.wasHidden = false;
    }
  }

  // --- petla ---

  private frame(timestamp: number): void {
    this.frameTime = timestamp;
    if (this.state === 'running' && this.trial) {
      this.advance(timestamp);
    }

    this.maybeReturnToMap();
    if (this.debugEnabled) this.refreshDebugLive();
    this.render();
    requestAnimationFrame((next) => this.frame(next));
  }

  /** Staly krok fizyki niezalezny od FPS: akumulator czasu i limit krokow na klatke. */
  private advance(timestamp: number): void {
    const trial = this.trial;
    if (!trial) return;

    if (this.lastTimestamp === 0) this.lastTimestamp = timestamp;
    let realDt = (timestamp - this.lastTimestamp) / 1000;
    this.lastTimestamp = timestamp;
    if (realDt > 0.25) realDt = 0.25;

    this.accumulator += realDt;
    const fixedDt = DEFAULT_TRIAL_OPTIONS.fixedDt;
    let steps = 0;
    while (this.accumulator >= fixedDt && steps < MAX_STEPS_PER_FRAME) {
      this.accumulator -= fixedDt;
      steps += 1;
      const outcome = trial.step();
      if (outcome !== 'running') {
        this.state = outcomeToState(outcome);
        const won = outcome === 'won';
        const time = trial.elapsedSec;
        const improved = won
          ? submitRecord(this.records, this.currentChallengeId(), time)
          : false;
        // Zaliczenie trasy otwiera nastepna w trybie podrozy.
        const unlocked = won ? this.recordWin() : null;

        // Animacja wyniku: sukces lub porazka w miejscu robota.
        const com = computeCenterOfMass(trial.robot.chassis, robotBodies(trial.robot));
        this.outcomeEffect = {
          outcome: won ? 'won' : 'failed',
          startedAt: timestamp,
          x: com.x,
          y: com.y,
        };

        // Haptyka: krotka wibracja na telefonie (iOS nie wspiera - wtedy nic).
        vibrateOutcome(won ? 'won' : 'failed');
        // Dzwiek wyniku: fanfara sukcesu albo "brzeczenie" porazki (glosniczek).
        playOutcome(won ? 'won' : 'failed', this.soundMuted);

        // Po sukcesie wracamy na mape tras (po animacji sukcesu).
        if (won) this.scheduleReturnToMap();

        this.setStatus(won ? 'Sukces' : 'Porazka', won ? 'won' : 'failed');
        this.setMessage(
          won
            ? `${trial.reason} Czas: ${time.toFixed(2)} s${
                improved ? ' - nowy rekord!' : ''
              } (mistrz ${this.currentChallenge().parTimeSec.toFixed(2)} s).${
                unlocked ? ` Odblokowano nowa trase: ${unlocked.label}.` : ''
              }`
            : `${trial.reason} (${time.toFixed(2)} s)`,
          won ? 'info' : 'error',
        );
        if (this.debugEnabled) this.captureDebugReport(trial);
        this.accumulator = 0;
        this.refreshUi();
        if (unlocked) this.revealNode(unlocked.id);
        return;
      }
    }
    if (steps >= MAX_STEPS_PER_FRAME) this.accumulator = 0;
    this.setStatus(`Proba trwa (${trial.sample.t.toFixed(1)} s)`, undefined);
  }

  /**
   * Zapisuje zaliczenie biezacej trasy i zwraca trase odblokowana tym zaliczeniem
   * (null, gdy gracz powtarza trase albo to juz ostatnia plansza).
   */
  private recordWin(): { id: string; label: string } | null {
    const order = this.challengeOrder();
    const before: Progress = { completed: [...this.progress.completed] };
    markCompleted(this.progress, this.currentChallengeId());
    const unlockedId = newlyUnlocked(order, before, this.progress);
    if (!unlockedId) return null;
    const challenge = CHALLENGES.find((entry) => entry.id === unlockedId);
    if (!challenge) return null;
    return { id: challenge.id, label: challenge.label };
  }

  /** Rysuje tylko widoczny widok; ekran startowy nie ma plotna. */
  private render(): void {
    if (this.view === 'start') return;
    try {
      if (this.view === 'track') this.renderTrack();
      else this.renderWorkshop();
    } catch (error) {
      // Blad jednej klatki nie moze ubic petli rysowania (canvas zostalby pusty).
      console.error('Blad renderowania:', error);
    }
  }

  /** Plotno warsztatu: sama konstrukcja + podglad montazu. Bez toru. */
  private renderWorkshop(): void {
    const { width, height } = this.stage.syncSize();
    this.fitWorkshopCamera(width, height);
    this.camera.resize(width, height);
    const ctx = this.stage.begin('#0b0f16');

    drawBlueprint(
      ctx,
      this.camera,
      this.store.current,
      this.store.selectedPartId,
      this.editor.draggingPart,
    );

    if (this.editor.isPlacing || this.editor.isDragging) {
      drawSockets(ctx, this.camera, this.editor.previewSockets());
      const ghost = this.editor.ghost();
      if (ghost) drawGhost(ctx, this.camera, ghost);
    }
  }

  /** Plotno trasy: sama mapa; na starcie bez pojazdu, w probie z fizyka. */
  private renderTrack(): void {
    const { width, height } = this.trackStage.syncSize();
    const challenge = this.currentChallenge();
    this.fitTrackCamera(width);
    this.trackCamera.resize(width, height);
    const ctx = this.trackStage.begin('#0b0f16');

    if (this.trial) {
      // Wstrzas ekranu przy porazce - tylko swiat, nie efekt (blysk ma kryc plotno).
      const shake = this.outcomeEffect
        ? outcomeShake(this.outcomeEffect.outcome, this.effectElapsed())
        : { x: 0, y: 0 };

      ctx.save();
      if (shake.x !== 0 || shake.y !== 0) ctx.translate(shake.x, shake.y);

      const com = computeCenterOfMass(this.trial.robot.chassis, robotBodies(this.trial.robot));
      drawChallenge(ctx, this.trackCamera, challenge, { comMarker: { x: com.x, y: com.y } });
      drawProps(ctx, this.trackCamera, this.propPoses(this.trial.props));
      drawRobot(ctx, this.trackCamera, this.trial.robot);
      ctx.restore();

      this.drawOutcome(ctx);
      return;
    }

    // Podglad trasy BEZ pojazdu - zamiast robota zaznaczamy miejsce konstrukcji.
    drawChallenge(ctx, this.trackCamera, challenge);
    drawProps(ctx, this.trackCamera, this.initialPropPoses(challenge));
    drawStartMarker(ctx, this.trackCamera, {
      x: this.startMarkerX(challenge),
      groundY: challenge.groundTopY,
      radius: 0.25,
      label: 'START',
    });
  }

  /** Srodek (os X) miejsca, gdzie na starcie trasy stanie konstrukcja gracza. */
  private startMarkerX(challenge: ChallengeDefinition): number {
    const blueprint = this.store.current;
    if (blueprint.parts.length === 0) return challenge.startX + 0.5;

    const bounds = blueprintBoundsGrid(blueprint);
    const origin = computeOrigin(blueprint, {
      startX: challenge.startX,
      groundTopY: challenge.groundTopY,
    });
    return origin.x + gridToMeters((bounds.minX + bounds.maxX) / 2);
  }

  /** Ile sekund minelo od zakonczenia proby (0, gdy brak efektu). */
  private effectElapsed(): number {
    if (!this.outcomeEffect) return 0;
    return Math.max(0, (this.frameTime - this.outcomeEffect.startedAt) / 1000);
  }

  /** Animacja sukcesu/porazki - rysowana, dopoki trwa jej czas. */
  private drawOutcome(ctx: CanvasRenderingContext2D): void {
    const effect = this.outcomeEffect;
    if (!effect) return;
    const elapsed = this.effectElapsed();
    if (elapsed > outcomeEffectDuration(effect.outcome)) return;
    drawOutcomeEffect(ctx, this.trackCamera, {
      outcome: effect.outcome,
      x: effect.x,
      y: effect.y,
      t: elapsed,
    });
  }

  /** Pozycje dynamicznych przeszkod w probie (po fizyce). */
  private propPoses(props: readonly PropHandle[]): PropPose[] {
    return props.map((prop) => {
      const position = prop.body.getPosition();
      return {
        x: position.x,
        y: position.y,
        angle: prop.body.getAngle(),
        w: prop.definition.w,
        h: prop.definition.h,
      };
    });
  }

  /** Pozycje startowe przeszkod w trybie budowania (bez fizyki). */
  private initialPropPoses(challenge: ChallengeDefinition): PropPose[] {
    return (challenge.dynamicProps ?? []).map((prop) => ({
      x: prop.x,
      y: prop.y,
      angle: 0,
      w: prop.w,
      h: prop.h,
    }));
  }
}